import React from 'react';
import { connect } from 'react-redux';
import { mapState, color } from '@deerplusofficial/pos-helper';
import { Button, ButtonBase, Icon, EmbedScanner } from '@deerplusofficial/pos-components';
import styled, { css } from 'styled-components';
import _ from 'lodash';
import fp from 'lodash/fp';
import { withQueryMembers } from '../../model/Queries';
import actions from '../../actions';
import InputBase from '../Input/Input';
import ModalBase from '../Modal/Modal';

const Modal = styled(ModalBase)`
  max-width: 500px;
`;

const Container = styled.div`
  text-align: center;
`;

const Title = styled.div`
  margin-bottom: 16px;
  font-size: 20px;
`;

const Subtitle = styled.div`
  margin-bottom: 24px;
  color: ${color.text.title};
`;

const Total = styled.div`
  margin-bottom: 6px;
  font-size: 10px;
  color: ${color.text.info};
`;

const Points = styled.div`
  margin-bottom: 16px;
  font-size: 18px;
  color: ${color.text.title};
`;

const InputContainer = styled.div`
  margin: 0 auto;
  position: relative;
  width: 100%;
  max-width: 300px;
`;

const ScannerToggle = styled(ButtonBase)`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 45px;
  height: 45px;
  background-color: #fff;
`;

const ScannerIcon = styled(Icon).attrs({ icon: 'qrcode', size: '2x' })`
  vertical-align: middle;
  color: ${(props) => (props.active ? color.primary : color.text.secondary)};
`;

const Input = styled(InputBase)`
  padding: 10px 16px 10px ${(props) => (props.pre ? '64px' : '16px')};
  width: 100%;
  line-height: 1.5;
  border-radius: 4px;
  font-size: 20px;
  letter-spacing: 10px;
  text-align: center;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const Helper = styled.div`
  margin: 16px auto;
  color: ${color.text.error};
  ${(props) =>
    !props.active &&
    css`
      display: none;
    `};
`;

const List = styled.div`
  white-space: nowrap;
  overflow: auto;
`;

const Table = styled.table`
  text-align: left;
  min-width: 100%;
  border-collapse: collapse;
  border: 1px solid ${color.border};
  th,
  td {
    padding: 16px;
    border-bottom: 1px solid ${color.border};
  }
  th {
    text-align: left;
    background: ${color.background.light};
  }
`;

const initState = {
  isSubmitting: false,
  isSubmitted: false,
  input: '',
  members: [],
};

class MemberModal extends React.PureComponent {
  state = {
    ...initState,
    isScannerActivated: Boolean(window.localStorage.getItem('isMemberModalScannerActivated')),
  };

  onInputChange = (e) => {
    e.preventDefault();

    const { value } = e.target;

    if (!/^\d*$/.test(value)) return;

    this.setState({ input: value, isSubmitted: false });
  };

  onPhoneNumberSubmit = async (value) => {
    const {
      queryMembers,
      order: { price },
    } = this.props;
    const query = typeof value === 'string' ? value : this.state.input;

    this.setState({ isSubmitting: true });

    const members = await queryMembers(query, price);

    this.setState({ members, isSubmitting: false, isSubmitted: true });
  };

  onModalClose = () => {
    const { toggleModal } = this.props;

    this.setState(initState);

    toggleModal();
  };

  onMemberApply = (data) => {
    const { updateMember, toggleModal } = this.props;

    updateMember(data);

    if (!data.points) {
      this.setState(initState);
      toggleModal();
      return;
    }

    this.setState({ members: [], input: '' });
  };

  onPointsUse = () => {
    const { input } = this.state;
    const { usePoints, toggleModal } = this.props;

    usePoints(Number(input));
    toggleModal();
    this.setState(initState);
  };

  onScannerToggle = () => {
    const { isScannerActivated } = this.state;

    if (isScannerActivated) {
      this.setState({ isScannerActivated: false });
      window.localStorage.removeItem('isMemberModalScannerActivated');
      return;
    }

    this.setState({ isScannerActivated: true });
    window.localStorage.setItem('isMemberModalScannerActivated', 1);
  };

  get error() {
    const { input, members, isSubmitted } = this.state;
    const {
      member: { points, id: memberId },
      order: { price },
    } = this.props;

    if (_.size(memberId)) {
      const inputPoints = Number(input);
      return inputPoints > points || inputPoints > price;
    }

    return isSubmitted && !_.size(members) && _.size(input);
  }

  get isLoggedIn() {
    const {
      member: { id },
    } = this.props;

    return Boolean(_.size(id));
  }

  get isScannable() {
    const {
      member: { showModal },
    } = this.props;
    const { members } = this.state;
    return _.every([!this.isLoggedIn, this.state.isScannerActivated, !_.size(members), showModal], Boolean);
  }

  renderMembers = (members) => (
    <List>
      <Table>
        <thead>
          <tr>
            <th />
            <th>姓名</th>
            <th>暱稱</th>
            <th>世代 Point</th>
            <th>手機</th>
          </tr>
        </thead>
        <tbody>
          {_.map(members, ({ id, first_name, last_name, phone, points, total, can_redeem }) => {
            const name = `${first_name} ${last_name}`;
            return (
              <tr key={id}>
                <td>
                  <Button
                    primary
                    dense
                    onClick={() => this.onMemberApply({ id, points, total, name })}
                  >
                    確認
                  </Button>
                </td>
                <td>{first_name}</td>
                <td>{last_name}</td>
                <td>{total}</td>
                <td>{phone}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </List>
  );

  render() {
    const { isSubmitting, isScannerActivated, input, members } = this.state;
    const {
      member: { name, points, total, showModal: active },
    } = this.props;

    return (
      <React.Fragment>
        <Modal active={active} onToggleModal={this.onModalClose}>
          <Container>
            <Title>{this.isLoggedIn ? name : '登入會員'}</Title>
            {!this.isLoggedIn && <Subtitle>輸入會員電話號碼來查詢</Subtitle>}
            {_.size(members) ? (
              this.renderMembers(members)
            ) : (
              <React.Fragment>
                {this.isLoggedIn && <Total>世代 Point 餘額：{total} 點</Total>}
                {this.isLoggedIn && <Points>此次訂單可折抵：{points} 點</Points>}
                <InputContainer>
                  <Input
                    pre={!this.isLoggedIn}
                    value={this.isScannable ? '已啟用掃瞄器' : input}
                    onChange={this.onInputChange}
                    disabled={isSubmitting || this.isScannable}
                    placeholder={this.isLoggedIn ? '點擊輸入使用額度' : ''}
                  />
                  {!this.isLoggedIn && (
                    <ScannerToggle onClick={this.onScannerToggle}>
                      <ScannerIcon active={isScannerActivated} />
                    </ScannerToggle>
                  )}
                </InputContainer>
                <Helper active={this.error}>{this.isLoggedIn ? '餘額不足或是超過可折抵金額' : '查無帳號'}</Helper>
                <Actions>
                  {this.isLoggedIn ? (
                    <Button primary disabled={this.error} onClick={this.onPointsUse}>
                      套用
                    </Button>
                  ) : (
                    <Button
                      primary
                      disabled={_.size(input) < 8}
                      loading={isSubmitting}
                      onClick={this.onPhoneNumberSubmit}
                    >
                      查詢
                    </Button>
                  )}
                </Actions>
              </React.Fragment>
            )}
          </Container>
        </Modal>
        {this.isScannable && <EmbedScanner onDetect={this.onPhoneNumberSubmit} />}
      </React.Fragment>
    );
  }
}

const {
  reset: resetMember,
  update: updateMember,
  modal: { toggle: toggleModal },
} = actions.member;
const {
  points: { use: usePoints },
} = actions.order;

export default fp.compose(
  connect(mapState(['member', 'order']), { resetMember, updateMember, usePoints, toggleModal }),
  withQueryMembers()
)(MemberModal);



// WEBPACK FOOTER //
// ./src/components/MemberModal/MemberModal.js